import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Users, ArrowRightLeft, Loader2, CheckCircle2, XCircle, Send,
  ShieldCheck, AlertTriangle, Clock, Sparkles, Landmark, Copy,
  FileSignature, RefreshCw, Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN",
                        { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function authHeaders() {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function VCBMappings() {
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState(null);
  const [mappings, setMappings] = useState({ outgoing: [], incoming: [] });
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [xferTo, setXferTo] = useState("");
  const [xferAmount, setXferAmount] = useState(0);
  const [xferMemo, setXferMemo] = useState("");
  const [xferMode, setXferMode] = useState("wallet");
  const [xferBusy, setXferBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Module gate first — if VCB is disabled, redirect straight home
      // (endpoint returns 404 when module is offline).
      try {
        const mod = await axios.get(`${API}/vcb/module-status`,
                                       { headers: authHeaders() });
        if (mod.data && mod.data.enabled === false) {
          toast.info("Vinci Core Banking is not yet activated for your account.");
          nav("/wallet");
          return;
        }
      } catch (e) {
        if (e.response?.status === 404) {
          toast.info("Vinci Core Banking is not yet activated.");
          nav("/wallet");
          return;
        }
      }
      const [s, h, m] = await Promise.all([
        axios.get(`${API}/vcb/status`, { headers: authHeaders() }),
        axios.get(`${API}/vcb/wallet/health`, { headers: authHeaders() }),
        axios.get(`${API}/vcb/mappings/mine`, { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setHealth(h.data);
      setMappings(m.data);
    } catch (e) {
      if (e.response?.status === 401) { nav("/login?redirect=/vcb/mappings"); return; }
      toast.error(e.response?.data?.detail || "Could not load VCB desk");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!inviteEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setInviting(true);
    try {
      const r = await axios.post(`${API}/vcb/mappings/invite`,
                                    { child_email: inviteEmail.trim(),
                                       note: inviteNote },
                                    { headers: authHeaders() });
      toast.success(`Mapping invitation sent to ${inviteEmail}`);
      setInviteEmail(""); setInviteNote("");
      if (r.data?.accept_link && navigator.clipboard) {
        try { await navigator.clipboard.writeText(r.data.accept_link); } catch {}
      }
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
    finally { setInviting(false); }
  };

  const transfer = async () => {
    if (!xferTo.includes("@")) { toast.error("Enter counterparty email"); return; }
    if (!(Number(xferAmount) > 0)) { toast.error("Enter a positive amount"); return; }
    setXferBusy(true);
    try {
      const r = await axios.post(`${API}/vcb/transfer`,
                                    { counterparty_email: xferTo.trim(),
                                       amount: Number(xferAmount),
                                       memo: xferMemo,
                                       payment_mode: xferMode },
                                    { headers: authHeaders() });
      toast.success(`Transferred ${inr(xferAmount)} · ${xferMode.toUpperCase()} · ref ${r.data.reference}`, {
        description: `Balance after: ${inr(r.data.payer_balance_after)} · 0% fee across all rails`,
      });
      setXferTo(""); setXferAmount(0); setXferMemo("");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
    finally { setXferBusy(false); }
  };

  const revoke = async (m) => {
    if (!confirm(`Revoke mapping with ${m.child_email}?`)) return;
    try {
      await axios.post(`${API}/vcb/mappings/${m.id}/revoke`, {},
                          { headers: authHeaders() });
      toast.success("Mapping revoked");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  const esignAction = async (m, verb) => {
    try {
      await axios.post(`${API}/vcb/mappings/${m.id}/esign/${verb}`, {},
                          { headers: authHeaders() });
      toast.success({ sync: "Signature status refreshed",
                      remind: "Signing reminder sent to pending parties",
                      dispatch: "Zoho-eSign envelope dispatched to both parties" }[verb]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || `eSign ${verb} failed`); }
  };

  const copyLink = async (m) => {
    const link = `${window.location.origin}/vcb/map/${m.token}`;
    try { await navigator.clipboard.writeText(link); toast.info("Accept link copied"); }
    catch { toast.error("Clipboard blocked"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] grid place-items-center">
        <Loader2 className="animate-spin text-[#D8B23E]" size={22} />
      </div>
    );
  }

  const notInducted = status?.status !== "inducted";
  const suspended = health?.vcb_status === "suspended";
  const belowFloor = !!health?.below_floor;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0] px-4 py-14 md:py-20">
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">
            Vinci Core Banking · Executive Desk
          </div>
          <h1 className="font-serif text-4xl md:text-5xl mt-2 leading-tight">
            Client Mapping Engine
          </h1>
          <p className="font-body text-sm text-[#C9C9C2] mt-2 max-w-2xl">
            Map your downstream customers once. Then send funds wallet-to-wallet
            with <span className="text-[#D8B23E]">0% handling charges</span> and
            no per-transaction ceilings — for the lifetime of your account.
          </p>
        </div>

        {/* Banking Health Ribbon */}
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                     className={`border rounded-sm p-5 md:p-6 grid md:grid-cols-4 gap-4 ${
                       suspended
                         ? "border-[#E5484D]/60 bg-[#1B0505]/40"
                         : belowFloor
                           ? "border-[#E5A93E]/60 bg-[#1B1401]/40"
                           : "border-[#2FB999]/40 bg-[#02150F]/40"
                     }`}
                     data-testid="vcb-health-ribbon">
          <StatBox label="VCB Status" value={health?.vcb_status || "—"}
                    tone={suspended ? "#E5484D" : belowFloor ? "#E5A93E" : "#2FB999"} />
          <StatBox label="Wallet Balance" value={inr(health?.balance)} />
          <StatBox label="Operating Floor" value={inr(health?.operating_floor)} />
          <StatBox label={suspended ? "Suspended" : "Grace Remaining"}
                    value={suspended ? "Reinstate needed"
                                        : belowFloor
                                          ? `${health?.grace_hours_remaining ?? 0}h`
                                          : "Healthy"}
                    tone={suspended ? "#E5484D" : belowFloor ? "#E5A93E" : "#F5F5F0"} />
        </motion.div>

        {notInducted && (
          <div className="border border-[#D8B23E]/60 bg-[#1B1401]/30 rounded-sm p-6 flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#D8B23E] shrink-0 mt-1" />
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">
                Induction Required
              </div>
              <div className="font-body text-sm text-[#C9C9C2] mt-1">
                Client mapping and 0% transfers are locked to VCB-inducted users.
                Ask your admin for an Executive Banking Invitation to unlock these
                features.
              </div>
            </div>
          </div>
        )}

        {/* Invite a sub-buyer */}
        <div className="border border-white/10 rounded-sm bg-[#0A0906] p-5 md:p-6 space-y-4">
          <h2 className="font-serif text-2xl inline-flex items-center gap-2">
            <Users size={16} className="text-[#D8B23E]" /> Map a Sub-Buyer
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Sub-buyer email</Label>
              <Input data-testid="vcb-map-email" value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="retailer@buyerfirm.com"
                      className="bg-black/40 border-white/20 text-[#F5F5F0]" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Personal note (optional)</Label>
              <Input data-testid="vcb-map-note" value={inviteNote}
                      onChange={(e) => setInviteNote(e.target.value)}
                      placeholder="Regular monthly settlements at 0%"
                      className="bg-black/40 border-white/20 text-[#F5F5F0]" />
            </div>
          </div>
          <Button data-testid="vcb-map-invite-btn"
                   onClick={invite} disabled={inviting || notInducted}
                   className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[10px] hover:bg-[#E5C450]">
            {inviting ? <Loader2 size={11} className="animate-spin" />
                       : <><Send size={11} className="mr-1.5" /> Send Mapping Invitation</>}
          </Button>
        </div>

        {/* Transfer form */}
        <div className={`border rounded-sm p-5 md:p-6 space-y-4 ${
          suspended ? "border-[#E5484D]/60 bg-[#1B0505]/30" : "border-[#D8B23E]/40 bg-[#0A0906]"
        }`}>
          <h2 className="font-serif text-2xl inline-flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-[#D8B23E]" /> Zero-Fee Transfer
          </h2>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Counterparty (mapped)</Label>
              <Input data-testid="vcb-xfer-to" value={xferTo}
                      onChange={(e) => setXferTo(e.target.value)}
                      placeholder="counterparty@vinci.demo"
                      className="bg-black/40 border-white/20 text-[#F5F5F0]" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Amount (INR)</Label>
              <Input data-testid="vcb-xfer-amount" type="number"
                      value={xferAmount} min={1} step={100}
                      onChange={(e) => setXferAmount(e.target.value)}
                      className="bg-black/40 border-white/20 text-[#D8B23E] font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Payment Rail</Label>
              <select data-testid="vcb-xfer-mode"
                       value={xferMode}
                       onChange={(e) => setXferMode(e.target.value)}
                       className="w-full bg-black/40 border border-white/20 text-[#F5F5F0] rounded-md px-3 py-2 text-sm font-body">
                <option value="wallet">Wallet · Closed Loop</option>
                <option value="upi">UPI</option>
                <option value="imps">IMPS</option>
                <option value="neft">NEFT</option>
                <option value="rtgs">RTGS</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">Memo (optional)</Label>
              <Input data-testid="vcb-xfer-memo" value={xferMemo}
                      onChange={(e) => setXferMemo(e.target.value)}
                      placeholder="March 2026 settlement · PO 8821"
                      className="bg-black/40 border-white/20 text-[#F5F5F0]" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-[11px] text-[#8A8A82] font-body italic inline-flex items-center gap-1.5">
              <Sparkles size={11} className="text-[#D8B23E]" />
              0% handling · Both parties must be VCB-inducted and mapped
            </div>
            <Button data-testid="vcb-xfer-btn"
                     onClick={transfer}
                     disabled={xferBusy || notInducted || suspended}
                     className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[10px] hover:bg-[#E5C450]">
              {xferBusy ? <Loader2 size={11} className="animate-spin" />
                         : <><ArrowRightLeft size={11} className="mr-1.5" /> Transfer {xferAmount ? inr(xferAmount) : ""}</>}
            </Button>
          </div>
        </div>

        {/* Mappings tables */}
        <MappingTable title="Outgoing · My Sub-Buyers" rows={mappings.outgoing}
                       role="parent" onRevoke={revoke} onCopy={copyLink} onEsign={esignAction} />
        <MappingTable title="Incoming · Mapped Into" rows={mappings.incoming}
                       role="child" onRevoke={revoke} onCopy={copyLink} onEsign={esignAction} />
      </div>
    </div>
  );
}

function StatBox({ label, value, tone }) {
  return (
    <div>
      <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">{label}</div>
      <div className="font-serif text-xl mt-0.5" style={{ color: tone || "#F5F5F0" }}>{value}</div>
    </div>
  );
}

const STATUS_TONE = {
  pending:  { color: "#D8B23E", icon: Clock },
  accepted: { color: "#2FB999", icon: CheckCircle2 },
  declined: { color: "#8A8A82", icon: XCircle },
  revoked:  { color: "#E5484D", icon: XCircle },
};

function EsignChip({ m }) {
  const e = m.esign;
  if (m.status !== "accepted") return null;
  if (!e) {
    return (
      <span data-testid={`esign-chip-${m.id}`}
            className="inline-flex items-center gap-1 font-grotesk text-[8px] uppercase tracking-[0.2em] text-[#8A8A82]">
        <FileSignature size={10} /> PDF only · not e-signed
      </span>
    );
  }
  const signed = (e.signers || []).filter((s) => s.status === "signed").length;
  const total = (e.signers || []).length || 2;
  const tone = e.status === "completed" ? "#2FB999"
    : ["declined", "expired", "recalled"].includes(e.status) ? "#E5484D" : "#D8B23E";
  const label = e.status === "completed" ? "Countersigned · Zoho eSign"
    : e.status === "partially_signed" ? `Awaiting countersign · ${signed}/${total}`
    : e.status === "sent" ? `Awaiting signatures · ${signed}/${total}`
    : `eSign ${e.status}`;
  return (
    <span data-testid={`esign-chip-${m.id}`} title={(e.signers || []).map((s) => `${s.email}: ${s.status}`).join(" · ")}
          className="inline-flex items-center gap-1 font-grotesk text-[8px] uppercase tracking-[0.2em]"
          style={{ color: tone }}>
      <FileSignature size={10} /> {label}
    </span>
  );
}

function MappingTable({ title, rows, role, onRevoke, onCopy, onEsign }) {
  return (
    <div className="space-y-2">
      <h3 className="font-serif text-xl">{title}</h3>
      {rows.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-sm p-6 text-[11px] text-[#8A8A82] italic text-center font-body">
          No mappings yet.
        </div>
      ) : (
        <div className="rounded-sm border border-white/10 bg-[#0A0906] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 bg-[#12100A]">
              <tr className="text-left font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">
                <th className="py-3 px-3">Counterparty</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Created</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const tone = STATUS_TONE[m.status] || STATUS_TONE.pending;
                const Icon = tone.icon;
                const cp = role === "parent" ? m.child_email : m.parent_email;
                return (
                  <tr key={m.id} className="border-t border-white/5">
                    <td className="py-3 px-3">
                      <div className="font-body text-sm text-[#F5F5F0]">{cp}</div>
                      {m.note && (
                        <div className="text-[10px] text-[#8A8A82] italic mt-0.5 max-w-md truncate">{m.note}</div>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <span className="inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em]"
                              style={{ color: tone.color }}>
                        <Icon size={11} /> {m.status}
                      </span>
                      <div className="mt-1"><EsignChip m={m} /></div>
                    </td>
                    <td className="py-3 px-3 text-[11px] font-mono text-[#C9C9C2]">
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {m.status === "pending" && role === "parent" && (
                          <button title="Copy accept link"
                                   onClick={() => onCopy(m)}
                                   className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E]">
                            <Copy size={12} />
                          </button>
                        )}
                        {role === "parent" && ["pending", "accepted"].includes(m.status) && (
                          <button title="Revoke mapping"
                                   onClick={() => onRevoke(m)}
                                   className="p-1.5 text-[#8A8A82] hover:text-[#E5484D]">
                            <XCircle size={12} />
                          </button>
                        )}
                        {m.status === "accepted" && m.esign && ["sent", "partially_signed"].includes(m.esign.status) && (
                          <>
                            <button title="Refresh signature status"
                                     data-testid={`esign-sync-${m.id}`}
                                     onClick={() => onEsign(m, "sync")}
                                     className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E]">
                              <RefreshCw size={12} />
                            </button>
                            <button title="Remind pending signers"
                                     data-testid={`esign-remind-${m.id}`}
                                     onClick={() => onEsign(m, "remind")}
                                     className="p-1.5 text-[#8A8A82] hover:text-[#D8B23E]">
                              <Bell size={12} />
                            </button>
                          </>
                        )}
                        {m.status === "accepted" && (!m.esign || ["declined", "expired", "recalled"].includes(m.esign.status)) && (
                          <button title="Send for Zoho eSign countersignature"
                                   data-testid={`esign-dispatch-${m.id}`}
                                   onClick={() => onEsign(m, "dispatch")}
                                   className="p-1.5 text-[#8A8A82] hover:text-[#2FB999]">
                            <FileSignature size={12} />
                          </button>
                        )}
                        {m.status === "accepted" && (
                          <a title={m.esign?.status === "completed" ? "Download countersigned agreement" : "Download Mapping Agreement"}
                              data-testid={`agreement-download-${m.id}`}
                              href={`${API}/vcb/mappings/${m.id}/agreement.pdf`}
                              target="_blank" rel="noreferrer"
                              className="p-1.5 text-[#8A8A82] hover:text-[#2FB999]">
                            <Landmark size={12} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Public accept-mapping page ─────────────────────────────────────────
export function VCBMapAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vcb/mappings/preview/${token}`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Mapping not found"));
  }, [token]);

  const isLoggedIn = () =>
    !!(localStorage.getItem("token") || localStorage.getItem("access_token"));

  const act = async (verb) => {
    if (!isLoggedIn()) { nav(`/login?redirect=/vcb/map/${token}`); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/vcb/mappings/${verb}/${token}`, {},
                          { headers: authHeaders() });
      toast.success(`Mapping ${verb === "accept" ? "accepted" : "declined"}`);
      nav("/vcb/mappings");
    } catch (e) { toast.error(e.response?.data?.detail || `${verb} failed`); }
    finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] text-[#F5F5F0] grid place-items-center px-4 text-center">
        <div>
          <AlertTriangle size={26} className="mx-auto text-[#E5A93E] mb-2" />
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#E5A93E]">Mapping Unavailable</div>
          <h1 className="font-serif text-3xl mt-2">{error}</h1>
        </div>
      </div>
    );
  }
  if (!preview) return <div className="min-h-screen bg-[#050505] grid place-items-center"><Loader2 className="animate-spin text-[#D8B23E]" size={20} /></div>;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0] grid place-items-center px-4 py-14">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                   className="max-w-lg w-full border border-[#D8B23E]/40 rounded-sm bg-gradient-to-br from-[#0A0906] via-[#12100A] to-[#0A0906] p-8 md:p-10 space-y-6">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Vinci Core Banking</div>
          <h1 className="font-serif text-3xl mt-2">Client Mapping Invitation</h1>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm font-body">
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">From</div>
            <div className="text-[#F5F5F0]">{preview.parent_email}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">To</div>
            <div className="text-[#F5F5F0]">{preview.child_email}</div>
          </div>
        </div>
        {preview.note && (
          <div className="border-l-2 border-[#D8B23E] bg-black/40 px-4 py-3 font-body text-sm italic text-[#C9C9C2]">
            {preview.note}
          </div>
        )}
        <div className="border-t border-white/5 pt-4 text-[11px] font-body text-[#8A8A82] italic inline-flex items-center gap-2">
          <Landmark size={12} className="text-[#D8B23E]" />
          Accepting binds you and {preview.parent_email} into permanent 0% wallet-to-wallet transfer capacity.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button data-testid="vcb-map-accept-btn"
                   onClick={() => act("accept")}
                   disabled={busy || preview.status !== "pending"}
                   className="bg-[#D8B23E] text-black font-grotesk uppercase tracking-[0.28em] text-[10px] hover:bg-[#E5C450]">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <>Accept Mapping</>}
          </Button>
          <Button variant="outline" data-testid="vcb-map-decline-btn"
                   onClick={() => act("decline")} disabled={busy || preview.status !== "pending"}
                   className="border-white/20 text-[#C9C9C2] font-grotesk uppercase tracking-[0.28em] text-[10px]">
            Decline
          </Button>
          {preview.status !== "pending" && (
            <div className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#8A8A82]">
              Already {preview.status}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
